import type { AlertIssue } from '../store/types';
import {
  normalizeMilestoneNameForSelector,
  normalizePhaseNameForSelector
} from './fieldMapper';

/** Aligns with `data-milestone-name-key` / fieldRef milestone segment (spaces → underscores). */
function milestoneNameKeyAttr(name: string): string {
  return name.replace(/\s+/g, '_');
}

function normalizeRefKey(s: string): string {
  return s.toLowerCase().replace(/[\s_-]+/g, '_');
}

function milestoneKeyMatches(
  milestoneDisplayName: string,
  milestoneTokenFromRef: string
): boolean {
  const variants = normalizeMilestoneNameForSelector(milestoneTokenFromRef);
  const domKey = milestoneNameKeyAttr(milestoneDisplayName);
  const n = normalizeRefKey(domKey);
  return variants.some(v => normalizeRefKey(v) === n);
}

/**
 * DOM `data-milestone-phase-key` uses underscores for spaces; fieldRef uses phase tokens
 * that `normalizePhaseNameForSelector` expands — compare normalized keys like fieldHighlighter.
 */
function phaseKeyMatches(
  domPhaseKey: string | undefined,
  phaseTokenFromRef: string
): boolean {
  if (!domPhaseKey) return false;
  const variants = normalizePhaseNameForSelector(phaseTokenFromRef);
  const n = normalizeRefKey(domPhaseKey);
  return variants.some(v => normalizeRefKey(v) === n);
}

export type MilestoneAlertCellSurface = 'header' | 'date';

/**
 * Returns true when this alert issue should highlight the given milestone column surface,
 * mirroring `fieldMapper` + `findMilestoneElements` in fieldHighlighter.ts.
 */
export function shouldHighlightMilestoneAlertCell(
  issue: AlertIssue,
  args: {
    milestoneName: string;
    phaseName?: string;
    cellSurface: MilestoneAlertCellSurface;
  }
): boolean {
  const { fieldRef, ruleId } = issue;

  if (ruleId === 'release-readiness') {
    if (!fieldRef) return false;
    const phased = fieldRef.match(/^(.+?)\.milestone\.(.+)$/);
    if (phased) {
      if (args.cellSurface !== 'date') return false;
      const [, phaseToken, milestoneToken] = phased;
      const domPhaseKey = args.phaseName
        ? milestoneNameKeyAttr(args.phaseName)
        : undefined;
      return (
        phaseKeyMatches(domPhaseKey, phaseToken) &&
        milestoneKeyMatches(args.milestoneName, milestoneToken)
      );
    }
    if (!milestoneKeyMatches(args.milestoneName, fieldRef)) return false;
    if (args.cellSurface !== 'date') return false;
    return true;
  }

  if (ruleId === 'date-past-due') {
    if (!fieldRef) return false;

    const phasedM = fieldRef.match(/^(.+?)\.milestone\.(.+)\.date$/);
    if (phasedM) {
      if (args.cellSurface !== 'date') return false;
      const [, phaseToken, milestoneToken] = phasedM;
      const domPhaseKey = args.phaseName
        ? milestoneNameKeyAttr(args.phaseName)
        : undefined;
      return (
        phaseKeyMatches(domPhaseKey, phaseToken) &&
        milestoneKeyMatches(args.milestoneName, milestoneToken)
      );
    }

    const nonPhased = fieldRef.match(/^milestone\.(.+)\.date$/);
    if (nonPhased) {
      if (args.cellSurface !== 'date') return false;
      return milestoneKeyMatches(args.milestoneName, nonPhased[1]);
    }

    return false;
  }

  return false;
}
